import NotificationsIcon from '@mui/icons-material/Notifications';
import TuneIcon from '@mui/icons-material/Tune';
import { TextField, MenuItem, ListItemButton, ButtonBase } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { COLOR } from '@/theme/colors';

import CustomOptionsInputModal from './components/CustomOptionsInputModal';
import {
    CustomOptionItem,
    CustomOptionItemType,
} from './components/CustomOptionsInputModal/types';
import { SelectWithCustomInputProps } from './types';

const TRIGGET_OPTION_VALUE = 'trigger';

export const SelectWithCustomInput = ({
    id,
    name,
    label,
    selectedOption,
    error,
    helperText,
    onBlur,
    onChange,
    testIdPrefix,
    sx,
    disabled,
    options,
    customOptions,
}: SelectWithCustomInputProps) => {
    const { t } = useTranslation();
    const [customOptionsVisible, setCustomOptionsVisible] = useState(false);
    const [selectOpened, setSelectOpened] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    const handleOpenCustomOptions = () => {
        setCustomOptionsVisible(true);
    };

    const handleCloseCustomOptions = () => {
        setCustomOptionsVisible(false);
    };

    const triggerOption = {
        value: TRIGGET_OPTION_VALUE,
        type: CustomOptionItemType.Trigger,
        text: t('Static:custom'),
    };

    const optionsWithTrigger = customOptions
        ? [...options, triggerOption]
        : options;

    const handleCustomOptionChange = (
        option: CustomOptionItem & { inputValue: string },
    ) => {
        onChange(option.inputValue + option.value);
        setCustomOptionsVisible(false);
        setSelectOpened(false);
    };

    return (
        <>
            <TextField
                inputRef={inputRef}
                select
                id={id}
                name={name}
                label={label}
                error={error}
                helperText={helperText}
                onBlur={onBlur}
                onChange={(e) => onChange(e.target.value)}
                value={selectedOption.value}
                data-test-id={`${testIdPrefix}-ContactsReminderTime`}
                sx={sx}
                disabled={disabled}
                SelectProps={{
                    open: selectOpened,
                    onOpen: () => setSelectOpened(true),
                    onClose: () => {
                        if (!customOptionsVisible) {
                            setSelectOpened(false);
                        }
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <ButtonBase
                            disableRipple
                            className="py-[10px] px-[15px] bg-transparent border-none"
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                                '&:active': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                            onClick={() => {
                                inputRef.current?.focus();
                                setSelectOpened(true);
                            }}
                        >
                            <NotificationsIcon htmlColor="rgba(0,0,0,0.54)" />
                        </ButtonBase>
                    ),
                }}
            >
                {optionsWithTrigger.map((option) => {
                    if (option.type === CustomOptionItemType.Trigger) {
                        return (
                            <ListItemButton
                                key={option.value}
                                data-test-id={`${testIdPrefix}-Alert${option.value}`}
                                onClick={handleOpenCustomOptions}
                                className="h-[36px]"
                            >
                                <div className="flex items-center gap-2">
                                    <TuneIcon htmlColor={COLOR.regentGray} />
                                    {option.text}
                                </div>
                            </ListItemButton>
                        );
                    }
                    return (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            data-test-id={`${testIdPrefix}-Alert${option.value}`}
                            onClick={() => {
                                setSelectOpened(false);
                            }}
                        >
                            {option.text}
                        </MenuItem>
                    );
                })}
            </TextField>
            {customOptionsVisible && (
                <CustomOptionsInputModal
                    isOpen={customOptionsVisible}
                    onClose={handleCloseCustomOptions}
                    onChange={handleCustomOptionChange}
                    options={customOptions || []}
                />
            )}
        </>
    );
};
